import type { ReactNode } from 'react';

/**
 * Tone-driven status pill. Replaces the seven-plus hand-rolled inline
 * clusters of the form
 *
 *   <span className="bg-success-soft/15 text-success-soft-fg ring-1 ring-success-soft/25 ...">
 *
 * Tones map to the soft status tokens, so the same component reads
 * correctly across light, dark, and colour-blind themes.
 *
 * Size 'sm' is for inline use in dense rows; 'md' is the default
 * (matching the existing in-app status pills).
 */

export type PillTone =
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'accent'
  | 'neutral';

export type PillSize = 'sm' | 'md';

const TONE_CLASSES: Record<PillTone, string> = {
  success: 'bg-success-soft/15 text-success-soft-fg',
  warning: 'bg-warning-soft/15 text-warning-soft-fg',
  danger: 'bg-danger-soft/15 text-danger-soft-fg',
  info: 'bg-info-soft/15 text-info-soft-fg',
  accent: 'bg-accent/10 text-accent',
  neutral: 'bg-neutral-soft/10 text-neutral-soft-fg',
};

const SIZE_CLASSES: Record<PillSize, string> = {
  sm: 'px-1.5 py-0.5 text-[10px]',
  md: 'px-2.5 py-0.5 text-[11px]',
};

export function Pill({
  tone = 'neutral',
  size = 'md',
  icon,
  children,
  className = '',
}: {
  tone?: PillTone;
  size?: PillSize;
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={[
        'inline-flex items-center gap-1 rounded-full font-semibold',
        TONE_CLASSES[tone],
        SIZE_CLASSES[size],
        className,
      ].join(' ')}
    >
      {icon ? (
        <span aria-hidden className="inline-flex shrink-0">
          {icon}
        </span>
      ) : null}
      {children}
    </span>
  );
}
